import React, { useCallback, useEffect } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useForm } from '@travelity/form';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SourceType } from '@travelity/api';

import schema from './source-modal.schema';
import BookingSourceForm from '../booking-source-form/booking-source-form';

const formId = 'source-modal';

export interface SourceFormItem {
    sourceType: SourceType;
    sourceName?: string;
    referral?: {
        id?: string;
        name?: any;
        email: string;
        company?: string;
    };
}

export interface SourceModalProps {
    handleCancel: () => void;
    handleConfirm: (v: SourceFormItem) => void;
    open: boolean;
    justifyContent: 'end' | 'center' | 'start';
    item?: Partial<SourceFormItem>;
}

const SourceModal: React.FC<SourceModalProps> = ({
    handleCancel,
    handleConfirm,
    open,
    justifyContent = 'center',
    item,
}) => {
    const { t } = useTranslation();

    const {
        Form,
        trigger,
        reset,
        watch,
        formState: { errors },
    } = useForm<SourceFormItem>({
        schema,
        isModal: true,
        mode: 'onChange',
        validateInitially: true,
        reValidateMode: 'onChange',
        onSubmit: useCallback(
            (data: SourceFormItem) => {
                handleConfirm(data);
            },
            [handleConfirm]
        ),
    });
    useEffect(() => {
        trigger();
    }, [watch('sourceType')]);

    useEffect(() => {
        if (open) {
            reset({
                sourceType: item?.sourceType || SourceType.DIRECT,
                sourceName: item?.sourceName || '',
                referral: item?.referral || {},
            });
            trigger();
        }
    }, [open, reset, trigger, item]);

    const errorFields = Object.values(errors)
        .map(error => error?.message)
        .filter(v => v) as string[];

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            sx={{
                '& .MuiDialog-container': { justifyContent },
            }}
            PaperProps={{
                sx: {
                    py: 1,
                    bgcolor: '#fff',
                    maxWidth: { xs: '100%', sm: '388px' },
                },
            }}
        >
            <DialogTitle
                sx={{
                    fontSize: '16px',
                    lineHeight: '16px',
                    fontWeight: 600,
                    color: '#2B395B',
                }}
                id="alert-dialog-title"
            >
                {t('Add Source')}
            </DialogTitle>
            <DialogContent
                sx={{
                    pr: 2,
                }}
            >
                <Form id={formId}>
                    <Stack
                        direction="column"
                        gap={2}
                        sx={{ pt: 2, width: '340px' }}
                    >
                        <BookingSourceForm />
                    </Stack>
                </Form>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    color="neutral"
                    variant="contained"
                    onClick={handleCancel}
                    sx={{
                        px: '26px',
                        py: '10px',
                    }}
                >
                    {t('Discard')}
                </Button>

                <Button
                    color="secondary"
                    variant="contained"
                    type="submit"
                    form={formId}
                    disabled={!!errorFields.length}
                    sx={{
                        py: '10px',
                        '&&&': { pointerEvents: 'initial' },
                    }}
                >
                    {t('Add Source')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default React.memo(SourceModal);
